import {
  Checkbox,
  InfoCard,
  Modal,
  PrimaryButton,
  SecondaryButton,
  SnackbarStatus,
  StatusBar,
  Typography,
} from '@weareopenr/component-library';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import chefPlating from 'src/assets/images/chef-plating.png';
import { commitVenuesSync } from 'src/api/commitVenuesSync/commitVenuesSync';
import type {
  IVenueChange,
  IVenueDelete,
  IVenueInsert,
  IVenueSyncDiff,
  IVenueUpdate,
} from 'src/api/getVenuesSyncStatus/getVenuesSyncStatus';
import { EProductLibraryStatus } from 'src/api/getProductLibrarySyncStatus/getProductLibrarySyncStatus';
import { NotificationType } from 'src/api/getNotifications/getNotifications';
import { QueryKeys, VENUE_SYNC_STATUS_QUERY } from 'src/hooks/QueryKeys';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useUpdateNotification } from 'src/hooks/useUpdateNotification/useUpdateNotification';
import { IVenueLibraryStatus } from 'src/hooks/useVenueLibraryStatus/useVenueLibraryStatus';
import { SYNC_MESSAGE_TEXT } from 'src/constants';
import { ButtonContainer } from '../ButtonContainer/ButtonContainer';
import styles from './VenueSyncReportModal.module.scss';
import { capitalise } from 'src/helpers/capitalise';

export const stepKeys: (keyof IVenueSyncDiff)[] = ['inserts', 'updates', 'deletes'];
export const pageTitles = ['New venues', 'Updated venues', 'Removed venues'];

interface IVenueSyncReportModalProps {
  isOpen: boolean;
  venues?: IVenueSyncDiff | null;
  eventId?: string;
  closeModal: () => void;
  rejectSync: (eventId: string) => void;
}

interface AdditionalProps extends Omit<IVenueSyncReportModalProps, 'isOpen'> {
  stepCount: number;
  setStepCount: Dispatch<SetStateAction<number>>;
}

// Turns a snake_case change field (e.g. "address_postal_code") into readable copy.
const formatFieldName = (field: string): string => field.replace(/_/g, ' ');

const InsertCard = ({ venue }: { venue: IVenueInsert }) => (
  <InfoCard
    label="New Venue"
    labelColour="green"
    title={`${venue.name} (${venue.code})`}
    details={[{ detail: venue?.address_summary ?? '', text: 'Address' }]}
    withDivider={false}
  />
);

const changesToFootnote = (changes: IVenueChange[]): string =>
  'Fields Changed: ' + changes.map(({ field }) => capitalise(formatFieldName(field))).join(', ');

const UpdateCard = ({ venue }: { venue: IVenueUpdate }) => (
  <InfoCard
    label="Updated"
    labelColour="blue"
    title={`${venue.name} (${venue.code})`}
    details={[{ detail: venue?.address_summary ?? '', text: 'Address' }]}
    footnote={changesToFootnote(venue.changes)}
    withDivider={!!venue.address_summary}
  />
);

const DeleteCard = ({ venue }: { venue: IVenueDelete }) => (
  <InfoCard label="Removed" labelColour="orange" title={`${venue.name}`} />
);

const VenueList = ({ stepCount, venues }: { stepCount: number; venues: IVenueSyncDiff }) => {
  const emptyMessage = `No ${SYNC_MESSAGE_TEXT[stepCount]} venues`;

  const cards: JSX.Element[] = (() => {
    if (stepCount === 0) return venues.inserts.map((venue) => <InsertCard key={venue.external_id} venue={venue} />);
    if (stepCount === 1) return venues.updates.map((venue) => <UpdateCard key={venue.venue_id} venue={venue} />);
    return venues.deletes.map((venue) => <DeleteCard key={venue.venue_id} venue={venue} />);
  })();

  return (
    <div className={styles['card-list']}>
      {cards.length === 0 ? (
        <Typography size="medium" className={styles['empty-message']}>
          {emptyMessage}
        </Typography>
      ) : (
        cards
      )}
    </div>
  );
};

const stepCountFor = (venues: IVenueSyncDiff, stepCount: number): number => venues[stepKeys[stepCount]].length;

const Content = ({ venues, eventId, closeModal, rejectSync, stepCount, setStepCount }: AdditionalProps) => {
  const queryClient = useQueryClient();
  const [isChangeAccepted, setIsChangeAccepted] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { markNotificationRead } = useUpdateNotification();

  useEffect(() => {
    setIsChangeAccepted(false);
  }, [stepCount]);

  if (!venues) return null;

  const isLastStep = stepCount === stepKeys.length - 1;

  const reject = () => {
    if (eventId) rejectSync(eventId);
    closeModal();
  };

  const stepNext = () => {
    if (!isLastStep) setStepCount(stepCount + 1);
  };

  const acceptSync = async () => {
    if (!eventId) return;
    clearError();
    setIsSubmitting(true);
    try {
      await commitVenuesSync(eventId);
      queryClient.setQueryData(VENUE_SYNC_STATUS_QUERY, (old: IVenueLibraryStatus) => ({
        ...old,
        status: EProductLibraryStatus.SUCCESS,
      }));
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.venues] });

      markNotificationRead((notification) => notification.type === NotificationType.VENUE_SYNC);

      closeModal();
    } catch {
      showNotification({
        status: SnackbarStatus.ERROR,
        message: 'An error has occurred; sync changes could not be accepted.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <Typography size="medium" weight="regular" className={styles['sync-steps']}>
        {`Step ${stepCount + 1} of ${stepKeys.length}`}
      </Typography>
      <Typography size="xxlarge" weight="semi-bold">
        {pageTitles[stepCount]}
      </Typography>
      <Typography className={styles['subtitle']}>
        Review the venue changes before either accepting or rejecting the sync. No changes will be made until you have
        accepted the sync.
      </Typography>

      <VenueList stepCount={stepCount} venues={venues} />

      <Checkbox
        label={`I acknowledge that I am accepting ${stepCountFor(venues, stepCount)} ${
          SYNC_MESSAGE_TEXT[stepCount]
        } venues`}
        name="acknowledge-step"
        labelWeight="regular"
        onChange={() => setIsChangeAccepted((prev) => !prev)}
        checked={isChangeAccepted}
      />

      <ButtonContainer
        left={
          <SecondaryButton data-testid="reject-button" buttonStyle="outline" onClick={reject} size="full-width">
            Reject Sync
          </SecondaryButton>
        }
        right={
          <PrimaryButton
            onClick={isLastStep ? acceptSync : stepNext}
            size="full-width"
            disabled={!isChangeAccepted || isSubmitting}
            data-testid="next-accept-button"
            loading={isSubmitting}
          >
            {isLastStep ? 'Accept Sync' : 'Next'}
          </PrimaryButton>
        }
      />
      <div className={styles['error-banner']}>
        {errorMessage && <StatusBar message={errorMessage} variant="error" />}
      </div>
    </div>
  );
};

export const VenueSyncReportModal = ({
  isOpen,
  venues,
  eventId,
  closeModal,
  rejectSync,
}: IVenueSyncReportModalProps) => {
  const [stepCount, setStepCount] = useState<number>(0);

  useEffect(() => {
    if (!isOpen) setStepCount(0);
  }, [isOpen]);

  const stepBack = () => {
    if (stepCount > 0) setStepCount(stepCount - 1);
  };

  return (
    <Modal
      open={isOpen && !!venues}
      closeModal={closeModal}
      image={chefPlating}
      imageAlt="chef"
      onBack={stepCount > 0 ? stepBack : undefined}
    >
      <Content
        venues={venues}
        eventId={eventId}
        closeModal={closeModal}
        rejectSync={rejectSync}
        stepCount={stepCount}
        setStepCount={setStepCount}
      />
    </Modal>
  );
};
